import { CommonConstant, NEGATIVE_ONES } from '@utility/constants/common';
import { DIVISION_FILTER_PARAM } from '@features/account-view/scope';
import type { OrderFilterValues } from './filters';

export type OrderListFilter = OrderFilterValues & {
  page?: number;
  limit?: number;
  search?: string;
  sort?: { time_acknowledged?: string };
  /** The legacy multi-select; travels as one comma-joined `marketplace`. */
  marketplaces?: string[];
  oos?: boolean | null;
  tab?: string;
} & Record<string, unknown>;

/* `status` is named because the cache keys off it: see the list hook. */
export type OrderListParams = Record<string, unknown> & { status?: string };

/* `-1` is how the legacy dropdowns spell "any", which the API reads as unset. */
function isAnyOption(value: unknown): boolean {
  return (NEGATIVE_ONES as readonly unknown[]).includes(value);
}

/* The V2 API ignores some parameters; asking for them would only mislead. */
function withoutUnsupported(
  filter: OrderListFilter | undefined,
  notSupported: readonly string[],
): OrderListFilter | undefined {
  if (!filter || notSupported.length === 0) {
    return filter;
  }

  const kept = { ...filter };
  for (const key of notSupported) {
    delete kept[key];
    delete kept[`${key}_op`];
  }

  return kept;
}

export function buildOrderListParams(
  rawFilter: OrderListFilter | undefined,
  status: string | undefined,
  search: string | undefined,
  /* EN-4117: parameters the V2 API said it ignores; they are not sent. */
  notSupported: readonly string[] = [],
): OrderListParams {
  const filter = withoutUnsupported(rawFilter, notSupported);
  const params: OrderListParams = {
    ...filter,
    /* The header's search box wins over the copy kept in the filter. */
    search: (search || filter?.search || '').trim(),
    sort: null,
    status,
  };

  /* `oos: false` means "do not filter on stock", not "in stock". */
  if (params.oos === false) {
    params.oos = null;
  }

  if (filter?.items_total_qty) {
    params.sort_by = 'sku';
    params.sort_order = CommonConstant.sortDirection.ascending;
  } else {
    params.sort_by = 'time_acknowledged';
    params.sort_order = filter?.sort?.time_acknowledged;
  }

  if (filter?.marketplaces?.length) {
    params.marketplace = filter.marketplaces.join(',');
  }

  /* The division filter lives in the address bar; the service sends the active one. */
  delete params[DIVISION_FILTER_PARAM];

  for (const key of Object.keys(params)) {
    if (isAnyOption(params[key])) {
      params[key] = '';
    }
  }

  return params;
}

/*
 * The region is checked by key rather than value: "every region" and "not asked
 * yet" are both the empty string.
 */
export function canFetchOrderList(
  status: string | undefined,
  filter: OrderListFilter | undefined,
): boolean {
  return Boolean(
    status &&
      filter?.limit &&
      filter?.page &&
      Object.keys(filter).includes('marketplace_region_id'),
  );
}
